import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';

const API = 'https://api.starhubcreative.id';
const MONTH_NAMES = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
const DAY_NAMES = ['Min','Sen','Sel','Rab','Kam','Jum','Sab'];

function platformIcon(platforms) {
  if (platforms === 'instagram') return { icon: 'ti-brand-instagram', color: '#E1306C' };
  if (platforms === 'tiktok') return { icon: 'ti-brand-tiktok', color: '#000000' };
  if (platforms === 'both' || platforms === 'all') return { icon: 'ti-apps', color: colors.accent };
  return { icon: 'ti-brand-youtube', color: '#FF0000' };
}

function statusStyle(status) {
  if (status === 'done' || status === 'success') return { bg: colors.successMuted, color: colors.success, label: 'Selesai' };
  if (status === 'failed') return { bg: colors.dangerMuted, color: colors.danger, label: 'Gagal' };
  if (status === 'processing') return { bg: colors.accentMuted, color: colors.accentText, label: 'Diproses' };
  return { bg: colors.warningMuted, color: colors.warning, label: 'Menunggu' };
}

// Ritme upload dari DNA (2026-10-07). Target per minggu, hari & jam tersering channel acuan,
// dibandingkan dengan upload tiap channel minggu ini. Backend: GET /api/dna/:id/rhythm.
const DAY_FULL = { Min: 'Minggu', Sen: 'Senin', Sel: 'Selasa', Rab: 'Rabu', Kam: 'Kamis', Jum: 'Jumat', Sab: 'Sabtu' };
const hh = h => `${String(h).padStart(2, '0')}.00`;
const fmtDate = s => { const [, m, d] = s.split('-').map(Number); return `${d} ${MONTH_NAMES[m - 1].slice(0, 3)}`; };

function RhythmPanel({ dnaList, dnaId, setDnaId, rhythm, onScheduleAndCreate }) {
  const [showAll, setShowAll] = useState(false);
  if (!dnaList.length) {
    return (
      <div style={{ fontSize: 12, color: colors.textSecondary, background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: '12px 16px', marginBottom: 16 }}>
        <i className="ti ti-dna" style={{ marginRight: 6 }}></i>Tambahkan channel acuan di menu DNA Channel untuk melihat saran ritme dan jam upload di kalender ini.
      </div>
    );
  }
  const r = rhythm;
  const behind = r ? r.channels.filter(c => c.missing > 0) : [];
  const list = showAll ? (r?.channels || []) : behind.slice(0, 8);
  return (
    <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="ti ti-dna"></i>Ritme upload dari DNA
        </div>
        <select id="calendar-dna" aria-label="DNA acuan" value={dnaId} onChange={e => setDnaId(e.target.value)}
          style={{ width: 'auto', minWidth: 180, padding: '6px 10px', fontSize: 12, borderRadius: 8 }}>
          {dnaList.map(d => <option key={d.id} value={d.id}>{d.channel_name || d.channel_url}</option>)}
        </select>
      </div>

      {!r ? (
        <div style={{ fontSize: 12, color: colors.textSecondary }}>Memuat ritme...</div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: colors.textPrimary, lineHeight: 1.6 }}>
            <b>{r.dna.name}</b> upload sekitar <b>{r.perWeek != null ? String(r.perWeek).replace('.', ',') : '-'} video per minggu</b>
            {r.bestDays.length > 0 && <>, paling sering hari <b>{r.bestDays.map(d => DAY_FULL[d]).join(', ')}</b></>}
            {r.bestHour != null && <> jam <b>{hh(r.bestHour)} WIB</b></>}.
            {r.target != null && <> Target untuk tiap channel kamu: <b>{r.target} video per minggu</b>.</>}
          </div>

          {r.slots.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>Jadwalkan di slot rame berikutnya:</span>
              {r.slots.map(s => (
                <button key={s.date} title="Buka Buat konten dengan jadwal ini untuk semua channel"
                  onClick={() => onScheduleAndCreate && onScheduleAndCreate(s.date, s.hour != null ? `${String(s.hour).padStart(2, '0')}:00` : undefined)}
                  style={{ fontSize: 12, padding: '5px 10px', borderRadius: 8, border: `1px solid ${colors.warning}`, background: colors.warningMuted, color: colors.textPrimary, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <i className="ti ti-flame" style={{ color: colors.warning }}></i>{DAY_FULL[s.day]} {fmtDate(s.date)}{s.hour != null ? `, ${hh(s.hour)}` : ''}
                </button>
              ))}
            </div>
          )}

          {r.target != null && (
            <div>
              <div style={{ fontSize: 12, color: colors.textPrimary, marginBottom: 6 }}>
                Minggu ini ({fmtDate(r.week.start)} – {fmtDate(r.week.end)}):{' '}
                {r.behind === 0
                  ? <b style={{ color: colors.success }}>semua {r.channels.length} channel sudah memenuhi target.</b>
                  : <><b style={{ color: colors.warning }}>{r.behind} dari {r.channels.length} channel</b> masih kurang dari target.</>}
              </div>
              {list.length > 0 && (
                <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, maxHeight: 260, overflowY: 'auto' }}>
                  {list.map((c, i) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderTop: i ? `1px solid ${colors.border}` : 'none', fontSize: 12 }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.textPrimary }}>{c.name}</span>
                      <span style={{ color: colors.textSecondary, flexShrink: 0 }}>{c.done} tayang · {c.upcoming} terjadwal</span>
                      <span style={{ flexShrink: 0, fontWeight: 600, color: c.missing > 0 ? colors.warning : colors.success, minWidth: 80, textAlign: 'right' }}>
                        {c.missing > 0 ? `kurang ${c.missing}` : 'cukup'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {r.channels.length > behind.slice(0, 8).length && (
                <button onClick={() => setShowAll(v => !v)} style={{ marginTop: 6, background: 'transparent', border: 'none', color: colors.accentText, fontSize: 12, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                  {showAll ? 'Tampilkan yang kurang saja' : `Lihat semua ${r.channels.length} channel`}
                </button>
              )}
            </div>
          )}
          <div style={{ fontSize: 11, color: colors.textMuted }}>Hari rame channel acuan diberi tanda <i className="ti ti-flame" style={{ color: colors.warning }}></i> di kalender di bawah.</div>
        </>
      )}
    </div>
  );
}

export default function Calendar({ onScheduleAndCreate }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(new Date());
  const [selectedJob, setSelectedJob] = useState(null);
  const [dnaList, setDnaList] = useState([]);
  const [dnaId, setDnaIdState] = useState('');
  const [rhythm, setRhythm] = useState(null);

  // Pilihan DNA diingat per browser (kenyamanan saja; aman kalau localStorage diblokir).
  const setDnaId = (id) => { setDnaIdState(id); try { localStorage.setItem('calendar_dna', id); } catch (e) {} };
  useEffect(() => {
    axios.get(`${API}/api/dna`).then(r => {
      const ready = (r.data || []).filter(d => d.status === 'ready');
      setDnaList(ready);
      let saved = '';
      try { saved = localStorage.getItem('calendar_dna') || ''; } catch (e) {}
      setDnaIdState(ready.some(d => d.id === saved) ? saved : (ready[0]?.id || ''));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!dnaId) { setRhythm(null); return; }
    setRhythm(null);
    axios.get(`${API}/api/dna/${dnaId}/rhythm`).then(r => setRhythm(r.data)).catch(() => setRhythm(null));
  }, [dnaId]);
  const hotDays = new Set(rhythm?.bestDays || []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/jobs/report`);
      setJobs(res.data || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const jobsByDay = useMemo(() => {
    const map = {};
    jobs.forEach(job => {
      const d = new Date(job.created_at);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(job);
      }
    });
    return map;
  }, [jobs, year, month]);

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const goPrevMonth = () => setCursor(new Date(year, month - 1, 1));
  const goNextMonth = () => setCursor(new Date(year, month + 1, 1));
  const goToday = () => setCursor(new Date());

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ti ti-calendar" style={{ fontSize: 22 }}></i>Kalender Konten
          </h1>
          <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 2 }}>Lihat semua konten terjadwal dan riwayatnya per hari.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={goPrevMonth} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCard, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="ti ti-chevron-left" style={{ fontSize: 16, color: colors.textSecondary }}></i>
          </button>
          <span style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary, minWidth: 140, textAlign: 'center' }}>{MONTH_NAMES[month]} {year}</span>
          <button onClick={goNextMonth} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCard, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="ti ti-chevron-right" style={{ fontSize: 16, color: colors.textSecondary }}></i>
          </button>
          <button onClick={goToday} style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCard, color: colors.textSecondary, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Hari ini</button>
        </div>
      </div>

      <RhythmPanel dnaList={dnaList} dnaId={dnaId} setDnaId={setDnaId} rhythm={rhythm} onScheduleAndCreate={onScheduleAndCreate} />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textSecondary }}>Memuat kalender...</div>
      ) : (
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 16, overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(110px, 1fr))', gap: 8, minWidth: 760 }}>
            {DAY_NAMES.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: colors.textSecondary, paddingBottom: 6 }}>{d}</div>
            ))}
            {cells.map((day, idx) => {
              if (day === null) return <div key={`empty-${idx}`} />;
              const dayJobs = jobsByDay[day] || [];
              const isToday = isCurrentMonth && today.getDate() === day;
              const cellDate = new Date(year, month, day);
              const diffDays = Math.floor((cellDate - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);
              const isEmptyUpcoming = diffDays >= 0 && diffDays <= 6 && dayJobs.length === 0;
              return (
                <div key={day} style={{
                  background: isToday ? colors.accentMuted : isEmptyUpcoming ? colors.warningMuted : colors.bgCardMuted,
                  border: `1px solid ${isToday ? colors.accent : isEmptyUpcoming ? colors.warning : colors.border}`,
                  borderRadius: 10, padding: 8, minHeight: 110,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 500, color: isToday ? colors.accentText : colors.textSecondary }}>
                    {day}{isToday ? ' Â· hari ini' : ''}
                    </div>
                    <button onClick={() => { const dateStr = year + '-' + String(month + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0'); if (onScheduleAndCreate) onScheduleAndCreate(dateStr); }} title="Jadwalkan konten di tanggal ini" style={{ width: 18, height: 18, borderRadius: 5, border: 'none', background: colors.accentMuted, color: colors.accentText, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                      <i className="ti ti-plus" style={{ fontSize: 12 }}></i>
                    </button>
                  </div>
                  {diffDays >= 0 && hotDays.has(DAY_NAMES[cellDate.getDay()]) && (
                    <button onClick={() => onScheduleAndCreate && onScheduleAndCreate(
                        year + '-' + String(month + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0'),
                        rhythm?.bestHour != null ? `${String(rhythm.bestHour).padStart(2, '0')}:00` : undefined)}
                      title={`Hari rame ${rhythm?.dna?.name || 'channel acuan'} — klik untuk menjadwalkan di jam ini`}
                      style={{ width: '100%', marginBottom: 4, fontSize: 10, fontWeight: 600, padding: '2px 4px', borderRadius: 5, border: 'none', background: colors.warningMuted, color: colors.warning, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                      <i className="ti ti-flame" style={{ fontSize: 11 }}></i>{rhythm?.bestHour != null ? hh(rhythm.bestHour) : 'hari rame'}
                    </button>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {dayJobs.slice(0, 3).map(job => {
                      const p = platformIcon(job.platforms);
                      const s = statusStyle(job.status);
                      return (
                        <div key={job.id} onClick={() => setSelectedJob(job)}
                          style={{ background: colors.bgCard, borderRadius: 6, padding: '5px 7px', cursor: 'pointer', borderLeft: `3px solid ${s.color}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 1 }}>
                            <i className={`ti ${p.icon}`} style={{ fontSize: 12, color: p.color }}></i>
                            <span style={{ fontSize: 10, color: colors.textSecondary, fontWeight: 600 }}>
                              {new Date(job.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {job.title || 'Tanpa judul'}
                          </div>
                        </div>
                      );
                    })}
                    {dayJobs.length > 3 && (
                      <div style={{ fontSize: 10, color: colors.textSecondary, textAlign: 'center', padding: '2px 0' }}>
                        +{dayJobs.length - 3} lainnya
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
        {[
          { label: 'Selesai', color: colors.success },
          { label: 'Gagal', color: colors.danger },
          { label: 'Diproses', color: colors.accent },
          { label: 'Menunggu', color: colors.warning },
          { label: 'Belum ada jadwal (7 hari ke depan)', color: colors.warningMuted },
        ].map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.textSecondary }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />{s.label}
          </div>
        ))}
      </div>

      {selectedJob && (
        <div onClick={() => setSelectedJob(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: '100%', background: colors.bgCard, height: '100vh', overflowY: 'auto', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Detail Konten</h2>
              <i className="ti ti-x" style={{ fontSize: 20, color: colors.textSecondary, cursor: 'pointer' }} onClick={() => setSelectedJob(null)}></i>
            </div>

            {(() => {
              const s = statusStyle(selectedJob.status);
              return (
                <div style={{ background: s.bg, color: s.color, borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="ti ti-clock" style={{ fontSize: 15 }}></i>{s.label} Â· {new Date(selectedJob.created_at).toLocaleString('id-ID')}
                </div>
              );
            })()}

            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 12, color: colors.textSecondary, margin: '0 0 4px' }}>Judul</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary, margin: 0 }}>{selectedJob.title || 'Tanpa judul'}</p>
            </div>

            {selectedJob.youtube_url && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, color: colors.textSecondary, margin: '0 0 4px' }}>Sumber</p>
                <a href={selectedJob.youtube_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: colors.accentText, wordBreak: 'break-all' }}>{selectedJob.youtube_url}</a>
              </div>
            )}

            {selectedJob.error_message && (
              <div style={{ background: colors.dangerMuted, color: colors.danger, borderRadius: 8, padding: '10px 12px', fontSize: 12, marginBottom: 14 }}>
                <i className="ti ti-alert-triangle" style={{ fontSize: 14, marginRight: 6 }}></i>{selectedJob.error_message}
              </div>
            )}

            {selectedJob.channels && selectedJob.channels.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 8 }}>Channel YouTube</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {selectedJob.channels.map((ch, i) => {
                    const cs = statusStyle(ch.status);
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: colors.bgCardMuted, borderRadius: 8 }}>
                        <span style={{ fontSize: 12, color: colors.textPrimary }}>{ch.channel_name || 'Channel'}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: cs.bg, color: cs.color }}>{cs.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedJob.ig_post_id && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, color: colors.textSecondary, margin: '0 0 4px' }}>Instagram</p>
                <p style={{ fontSize: 12, color: colors.success, margin: 0 }}>Post ID: {selectedJob.ig_post_id}</p>
              </div>
            )}
            {selectedJob.ig_error && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, color: colors.danger, margin: 0 }}>IG Error: {selectedJob.ig_error}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
